/**
 * Sector operations — Connector client service.
 * R39 M11.a + M11.b (SO-P275).
 */

import { callable } from './callable'
import type {
  OpsConnector,
  OpsConnectorKind,
  OpsConnectorState,
  OpsConnectorEvent,
  ConnectorEventOutcome,
} from '../types/ops'

export interface CreateOpsConnectorInput {
  workspaceId: string
  sector?: string
  kind?: OpsConnectorKind | string
  name: string
  eventMap?: Record<string, string>
  opId?: string
}

export interface CreateOpsConnectorResult {
  connectorId: string
  plaintextSecret: string
  webhookUrl: string
}

export interface RotateOpsConnectorSecretInput {
  workspaceId: string
  connectorId: string
  opId?: string
}

export interface RotateOpsConnectorSecretResult {
  plaintextSecret: string
}

export interface UpdateOpsConnectorInput {
  workspaceId: string
  connectorId: string
  name?: string
  state?: OpsConnectorState
  eventMap?: Record<string, string>
  opId?: string
}

export interface DeleteOpsConnectorInput {
  workspaceId: string
  connectorId: string
  opId?: string
}

export interface GetOpsConnectorStatusInput {
  workspaceId: string
  connectorId: string
}

export interface OpsConnectorStatusResult {
  connector: Omit<OpsConnector, 'secretId'>
  countsByOutcome: {
    applied: number
    ignored: number
    error: number
  }
  last50Events: OpsConnectorEvent[]
}

export interface ReplayOpsConnectorEventInput {
  workspaceId: string
  connectorId: string
  eventId: string
  opId?: string
}

export interface ReplayOpsConnectorEventResult {
  replayed: boolean
  outcome: ConnectorEventOutcome
  reason?: string
}

export async function createOpsConnector(
  input: CreateOpsConnectorInput,
): Promise<CreateOpsConnectorResult> {
  return callable<CreateOpsConnectorInput, CreateOpsConnectorResult>('createOpsConnector', input)
}

export async function rotateOpsConnectorSecret(
  input: RotateOpsConnectorSecretInput,
): Promise<RotateOpsConnectorSecretResult> {
  return callable<RotateOpsConnectorSecretInput, RotateOpsConnectorSecretResult>(
    'rotateOpsConnectorSecret',
    input,
  )
}

export async function updateOpsConnector(
  input: UpdateOpsConnectorInput,
): Promise<{ updated: boolean }> {
  return callable<UpdateOpsConnectorInput, { updated: boolean }>('updateOpsConnector', input)
}

export async function deleteOpsConnector(
  input: DeleteOpsConnectorInput,
): Promise<{ deleted: boolean }> {
  return callable<DeleteOpsConnectorInput, { deleted: boolean }>('deleteOpsConnector', input)
}

export async function getOpsConnectorStatus(
  input: GetOpsConnectorStatusInput,
): Promise<OpsConnectorStatusResult> {
  return callable<GetOpsConnectorStatusInput, OpsConnectorStatusResult>(
    'getOpsConnectorStatus',
    input,
  )
}

export async function replayOpsConnectorEvent(
  input: ReplayOpsConnectorEventInput,
): Promise<ReplayOpsConnectorEventResult> {
  return callable<ReplayOpsConnectorEventInput, ReplayOpsConnectorEventResult>(
    'replayOpsConnectorEvent',
    input,
  )
}
